import { useQueryClient } from '@tanstack/react-query';
import { graphql } from '~/graphql';
import { runGraphqlQuery, useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import type {
  PaginatedFetchOptions,
  RouteSort,
} from '~/hooks/usePaginatedQuery';
import type { BuyListMatchInput } from '~/graphql/graphql';
import { MY_BUY_LIST_PRODUCTS_QUERY_KEY } from '~/api/platformProducts';

export const BUY_LISTS_QUERY_KEY = 'buyLists';
export const BUY_LIST_DETAIL_QUERY_KEY = 'buyListDetail';

export type BuyListQueryParams = {
  buyerIds?: string[];
  match?: BuyListMatchInput;
};

export const buyListItemSummaryFragment = graphql(`
  fragment BuyListItemSummary on BuyListItem {
    id
    quantity {
      value
      unit
    }
    effectivePiecesPerBundle
    platformProductsLength {
      id
      lengthDisplayValue
      isNominal
      length {
        nominalLengthInches
        actualLengthInches
      }
      platformProduct {
        code
        name
        nominalWidthInches
        actualWidthInches
        nominalThicknessInches
        actualThicknessInches
        actualLengthInches
        piecesPerBundle {
          value
          unit
        }
      }
    }
  }
`);

const myBuyListsQuery = graphql(`
  query MyBuyLists(
    $page: Int!
    $size: Int!
    $match: BuyListMatchInput
    $sort: [SortInput!]
  ) {
    myBuyLists(
      pageInput: { page: $page, size: $size }
      match: $match
      sort: $sort
    ) {
      paging {
        totalElements
        totalPages
        number
        size
      }
      content {
        id
        expectedShipDate
        note
        status
        location {
          id
          code
          name
          facility {
            id
            code
            address {
              addressLine1
              addressLine2
              city
              region
              zipCode
            }
          }
        }
        transportVehicle {
          id
          name
        }
        itemCount
        tallyCount
        items {
          ...BuyListItemSummary
        }
      }
    }
  }
`);

export const useGetMyBuyLists = (
  page: number,
  size: number,
  params?: BuyListQueryParams,
  sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const match = params?.match;

  return useGraphqlQuery(
    myBuyListsQuery,
    [BUY_LISTS_QUERY_KEY, 'mine', page, size, match ?? null, sort ?? null],
    { page, size, match, sort },
    options
  );
};

const myBuyListTargetsQuery = graphql(`
  query MyBuyListTargets($page: Int!, $size: Int!) {
    myBuyLists(pageInput: { page: $page, size: $size }) {
      paging {
        totalElements
        totalPages
        number
        size
      }
      content {
        id
        expectedShipDate
        note
        status
        location {
          id
          code
          name
          facility {
            id
            code
            address {
              addressLine1
              addressLine2
              city
              region
              zipCode
            }
          }
        }
        transportVehicle {
          id
          name
        }
        items {
          id
          platformProductsLength {
            id
          }
          quantity {
            value
            unit
          }
          effectivePiecesPerBundle
        }
      }
    }
  }
`);

export const useGetMyBuyListTargets = (
  page: number,
  size: number,
  _params?: BuyListQueryParams,
  _sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  return useGraphqlQuery(
    myBuyListTargetsQuery,
    [BUY_LISTS_QUERY_KEY, 'targets', page, size],
    { page, size },
    options
  );
};

const OPEN_BUY_LISTS_PAGE_SIZE = 100;

export const useFetchOpenBuyListTargets = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.fetchQuery({
      queryKey: [BUY_LISTS_QUERY_KEY, 'targets', 0, OPEN_BUY_LISTS_PAGE_SIZE],
      queryFn: ({ signal }) =>
        runGraphqlQuery(
          myBuyListTargetsQuery,
          { page: 0, size: OPEN_BUY_LISTS_PAGE_SIZE },
          signal
        ),
    });
};

const teamBuyListsQuery = graphql(`
  query TeamBuyLists(
    $page: Int!
    $size: Int!
    $filter: BuyListFilterInput
    $sort: [SortInput!]
  ) {
    teamBuyLists(
      pageInput: { page: $page, size: $size }
      filter: $filter
      sort: $sort
    ) {
      paging {
        totalElements
        totalPages
        number
        size
      }
      content {
        id
        expectedShipDate
        note
        status
        initiatingUser {
          id
          firstName
          lastName
        }
        location {
          id
          code
          name
          facility {
            id
            code
            address {
              addressLine1
              addressLine2
              city
              region
              zipCode
            }
          }
        }
        transportVehicle {
          id
          name
        }
        itemCount
        tallyCount
        items {
          ...BuyListItemSummary
        }
      }
    }
  }
`);

export const useGetTeamBuyLists = (
  page: number,
  size: number,
  params?: BuyListQueryParams,
  sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const buyerIds = params?.buyerIds?.length ? params.buyerIds : undefined;
  const match = params?.match;
  const filter = buyerIds || match ? { buyerIds, match } : undefined;

  return useGraphqlQuery(
    teamBuyListsQuery,
    [
      BUY_LISTS_QUERY_KEY,
      'team',
      page,
      size,
      buyerIds ?? null,
      match ?? null,
      sort ?? null,
    ],
    { page, size, filter, sort },
    options
  );
};

const buyListQuery = graphql(`
  query BuyList($id: ID!) {
    buyList(id: $id) {
      id
      expectedShipDate
      note
      status
      initiatingUser {
        id
        firstName
        lastName
      }
      location {
        id
        code
        name
        facility {
          id
          code
          address {
            addressLine1
            addressLine2
            city
            region
            zipCode
          }
        }
      }
      transportVehicle {
        id
        name
      }
      items {
        id
        platformProductsLength {
          id
          lengthDisplayValue
          isNominal
          latestPriceMid {
            amount
            currency
          }
          length {
            nominalLengthInches
            actualLengthInches
          }
          platformProduct {
            code
            name
            category
            standardUnitsCode
            nominalWidthInches
            actualWidthInches
            nominalThicknessInches
            actualThicknessInches
            actualLengthInches
            piecesPerBundle {
              value
              unit
            }
          }
        }
        quantity {
          value
          unit
        }
        effectivePiecesPerBundle
        piecesPerBundleOverride
        carts {
          buyer {
            id
            firstName
            lastName
          }
          quantity {
            value
            unit
          }
        }
      }
      tallies {
        __typename
        id
        ... on CannedBuyListTally {
          cannedTally {
            id
            name
          }
          tallyCount
        }
        platformProduct {
          id
          code
          name
          standardUnitsCode
          piecesPerBundle {
            value
            unit
          }
        }
        quantity {
          value
          unit
        }
        bundles {
          bundleCount
          quantity {
            value
            unit
          }
          platformProductsLength {
            id
            lengthDisplayValue
            isNominal
            length {
              id
              nominalLengthInches
              actualLengthInches
            }
            platformProduct {
              id
              nominalWidthInches
              actualWidthInches
              nominalThicknessInches
              actualThicknessInches
              actualLengthInches
              piecesPerBundle {
                value
                unit
              }
            }
          }
        }
      }
    }
  }
`);

export const useGetBuyList = (buyListId: string | null) => {
  return useGraphqlQuery(
    buyListQuery,
    [BUY_LIST_DETAIL_QUERY_KEY, buyListId ?? ''],
    { id: buyListId as string },
    { enabled: !!buyListId }
  );
};

const buyListQuoteComparisonQuery = graphql(`
  query BuyListQuoteComparison($buyListId: ID!, $page: Int!, $size: Int!) {
    buyListRequestForQuotes(
      buyListId: $buyListId
      pageInput: { page: $page, size: $size }
    ) {
      paging {
        totalElements
      }
      content {
        id
        status
        createdAt
        createdByUserName
        leadTimeDays
        useTallyPrice
        currency
        cannedTally {
          id
          name
        }
        cannedTallyCount
        tallyPlatformProduct {
          id
          name
          standardUnitsCode
          piecesPerBundle {
            value
            unit
          }
        }
        shippingLocation {
          id
          name
          code
          facility {
            id
            address {
              addressLine1
              addressLine2
              city
              region
              zipCode
              country
            }
          }
        }
        pickupPriceForTally {
          amount
          currency
        }
        deliveryPriceForTally {
          amount
          currency
        }
        items {
          id
          quantityInPieces
          piecesPerBundle
          pickupPrice {
            amount
            currency
          }
          deliveryPrice {
            amount
            currency
          }
          platformProductsLength {
            id
            lengthDisplayValue
            isNominal
            length {
              id
              displayValue
              actualLengthInches
              nominalLengthInches
            }
            platformProduct {
              id
              name
              code
              standardUnitsCode
              nominalWidthInches
              actualWidthInches
              nominalThicknessInches
              actualThicknessInches
              actualLengthInches
              piecesPerBundle {
                value
                unit
              }
            }
          }
        }
        quotes {
          id
          status
          negotiationAwaitingParty
          primaryContact {
            id
            ...PrimaryContact
          }
          recipients {
            id
            firstName
            lastName
            phoneNumber
            externalId
          }
          lockedRecipientIds
          rounds {
            ...QuoteTimelineRound
            ...QuoteOfferRound
          }
          currency
          leadTimeDays
          submittedAt
          sellingOrganization {
            id
            name
            sellingOrganizationCode
            facilities {
              id
              code
              address {
                addressLine1
                addressLine2
                city
                region
              }
              locations {
                id
                code
              }
            }
          }
          sellingOrganizationFacility {
            id
            code
            address {
              addressLine1
              addressLine2
              city
              region
            }
          }
          offeredPickupPriceForTally {
            amount
            currency
          }
          offeredDeliveryPriceForTally {
            amount
            currency
          }
          items {
            id
            quantityInPieces
            piecesPerBundle
            status
            offeredPickupPrice {
              amount
              currency
            }
            offeredDeliveryPrice {
              amount
              currency
            }
            order {
              id
            }
            platformProductsLength {
              id
              lengthDisplayValue
              isNominal
              length {
                id
                displayValue
                actualLengthInches
                nominalLengthInches
              }
              platformProduct {
                id
                name
                code
                standardUnitsCode
                nominalWidthInches
                actualWidthInches
                nominalThicknessInches
                actualThicknessInches
                actualLengthInches
                piecesPerBundle {
                  value
                  unit
                }
              }
            }
          }
        }
      }
    }
  }
`);

const ALL_REQUESTS_FOR_ONE_BUY_LIST_PAGE = { page: 0, size: 100 };

export const useGetBuyListQuoteComparison = (
  buyListId: string | undefined,
  options?: { enabled?: boolean }
) => {
  return useGraphqlQuery(
    buyListQuoteComparisonQuery,
    [BUY_LISTS_QUERY_KEY, 'quoteComparison', buyListId ?? ''],
    {
      buyListId: buyListId ?? '',
      page: ALL_REQUESTS_FOR_ONE_BUY_LIST_PAGE.page,
      size: ALL_REQUESTS_FOR_ONE_BUY_LIST_PAGE.size,
    },
    { enabled: !!buyListId && options?.enabled !== false }
  );
};

const createBuyListMutation = graphql(`
  mutation CreateBuyList(
    $locationId: ID!
    $transportVehicleId: ID!
    $expectedShipDate: String!
    $note: String
  ) {
    createBuyList(
      locationId: $locationId
      transportVehicleId: $transportVehicleId
      expectedShipDate: $expectedShipDate
      note: $note
    ) {
      id
      expectedShipDate
      note
      status
      location {
        id
        code
        name
        facility {
          id
          code
          address {
            addressLine1
            addressLine2
            city
            region
            zipCode
          }
        }
      }
      transportVehicle {
        id
        name
      }
      items {
        id
        platformProductsLength {
          id
        }
        quantity {
          value
          unit
        }
        effectivePiecesPerBundle
        piecesPerBundleOverride
      }
    }
  }
`);

export const useCreateBuyList = () => {
  return useGraphqlMutation(createBuyListMutation, [BUY_LISTS_QUERY_KEY]);
};

const addProductToBuyListMutation = graphql(`
  mutation AddProductToBuyList(
    $buyListId: ID!
    $platformProductsLengthId: ID!
    $quantity: QuantityInput!
    $piecesPerBundleOverride: Int
    $clearPiecesPerBundleOverride: Boolean
  ) {
    addProductToBuyList(
      buyListId: $buyListId
      platformProductsLengthId: $platformProductsLengthId
      quantity: $quantity
      piecesPerBundleOverride: $piecesPerBundleOverride
      clearPiecesPerBundleOverride: $clearPiecesPerBundleOverride
    ) {
      id
    }
  }
`);

export const useAddProductToBuyList = () => {
  return useGraphqlMutation(addProductToBuyListMutation, [
    BUY_LISTS_QUERY_KEY,
    BUY_LIST_DETAIL_QUERY_KEY,
    MY_BUY_LIST_PRODUCTS_QUERY_KEY,
  ]);
};

const removeProductFromBuyListMutation = graphql(`
  mutation RemoveProductFromBuyList(
    $buyListId: ID!
    $platformProductsLengthId: ID!
  ) {
    removeProductFromBuyList(
      buyListId: $buyListId
      platformProductsLengthId: $platformProductsLengthId
    ) {
      id
    }
  }
`);

export const useRemoveProductFromBuyList = () => {
  return useGraphqlMutation(removeProductFromBuyListMutation, [
    BUY_LISTS_QUERY_KEY,
    BUY_LIST_DETAIL_QUERY_KEY,
    MY_BUY_LIST_PRODUCTS_QUERY_KEY,
  ]);
};

const setCannedTallyOnBuyListMutation = graphql(`
  mutation SetCannedTallyOnBuyList(
    $buyListId: ID!
    $cannedTallyId: ID!
    $tallyCount: Int!
  ) {
    setCannedTallyOnBuyList(
      buyListId: $buyListId
      cannedTallyId: $cannedTallyId
      tallyCount: $tallyCount
    ) {
      id
    }
  }
`);

export const useSetCannedTallyOnBuyList = () => {
  return useGraphqlMutation(setCannedTallyOnBuyListMutation, [
    BUY_LISTS_QUERY_KEY,
    BUY_LIST_DETAIL_QUERY_KEY,
  ]);
};

const setRandomTallyOnBuyListMutation = graphql(`
  mutation SetRandomTallyOnBuyList(
    $buyListId: ID!
    $platformProductId: ID!
    $bundles: [RandomTallyBundleInput!]!
  ) {
    setRandomTallyOnBuyList(
      buyListId: $buyListId
      platformProductId: $platformProductId
      bundles: $bundles
    ) {
      id
    }
  }
`);

export const useSetRandomTallyOnBuyList = () => {
  return useGraphqlMutation(setRandomTallyOnBuyListMutation, [
    BUY_LISTS_QUERY_KEY,
    BUY_LIST_DETAIL_QUERY_KEY,
  ]);
};

const removeTallyFromBuyListMutation = graphql(`
  mutation RemoveTallyFromBuyList($buyListId: ID!, $buyListTallyId: ID!) {
    removeTallyFromBuyList(
      buyListId: $buyListId
      buyListTallyId: $buyListTallyId
    ) {
      id
    }
  }
`);

export const useRemoveTallyFromBuyList = () => {
  return useGraphqlMutation(removeTallyFromBuyListMutation, [
    BUY_LISTS_QUERY_KEY,
    BUY_LIST_DETAIL_QUERY_KEY,
  ]);
};

const updateBuyListStatusMutation = graphql(`
  mutation UpdateBuyListStatus($buyListId: ID!, $status: BuyListStatus!) {
    updateBuyListStatus(buyListId: $buyListId, status: $status) {
      id
      status
    }
  }
`);

export const useUpdateBuyListStatus = () => {
  return useGraphqlMutation(updateBuyListStatusMutation, [
    BUY_LISTS_QUERY_KEY,
    BUY_LIST_DETAIL_QUERY_KEY,
    MY_BUY_LIST_PRODUCTS_QUERY_KEY,
  ]);
};
